import { APITypesV1 } from "@cur8/api-client";
import { DeploymentLabel, RegionResolution } from "./region";

/**
 * The region holding this member's data, from `PatientDetails.dataRegion`.
 *
 * `undefined` means the backend did not state a region: the location index has no row for
 * the member, the index read failed, or the deployment predates the field. `null` and the
 * empty string collapse to the same answer, since neither names a region.
 */
export function memberDataRegion(details: APITypesV1.PatientDetails) {
  return details.dataRegion || undefined;
}

/**
 * What a member's name should read as. Falls back to the legacy identifier in their
 * physical ids, and never to a blank: the clinician is about to photograph a lesion and
 * has to be able to confirm who they are working on.
 */
export function memberDisplayName(
  details: APITypesV1.PatientDetails,
  fallback: string
) {
  const { displayName, firstName, lastName } = details.name ?? {};

  if (displayName) {
    return displayName;
  }

  if (firstName) {
    return lastName ? `${firstName} ${lastName}` : firstName;
  }

  const legacyId = details.physicalIds?.find((id) =>
    id.startsWith("cur8:id:legacy:")
  );

  return legacyId?.split(":")[3] || fallback;
}

/**
 * Whether this app should withhold a member's record, given the region it is serving and
 * the region holding the member's data.
 *
 * An unknown region on either side is no reason to withhold. A member with no location
 * index row would otherwise lose their record in every region at once, and a serving
 * region this app failed to resolve would withhold every member including the ones whose
 * data is right here. Withholding a record that is correct to show is the worse of the two
 * failures, so the guardrail only fires on two known regions that differ.
 */
export function shouldWithhold(
  servingRegion: DeploymentLabel | undefined,
  memberRegion: DeploymentLabel | undefined
) {
  return (
    servingRegion !== undefined &&
    memberRegion !== undefined &&
    servingRegion !== memberRegion
  );
}

export type MemberState =
  /** Either the member or this app's own region is still resolving. */
  | { status: "loading" }
  /** The member's data is held in another region, so nothing of their record may render. */
  | {
      status: "withheld";
      name: string;
      externalId: string;
      memberRegion: DeploymentLabel;
      servingRegion: DeploymentLabel;
    }
  | { status: "ready"; name: string; details: APITypesV1.PatientDetails }
  | { status: "failed"; name: string; message: string };

export type MemberLoad =
  | { status: "loading" }
  | { status: "loaded"; details: APITypesV1.PatientDetails }
  | { status: "failed"; message: string };

/**
 * True once a page may render this member's lesions. A failed read is included: the region
 * check could not run, and that is not a reason to stop a visit. The lesion list is keyed on
 * the identifier from the route, so it loads whether or not the member's name did.
 */
export function mayRenderMember(member: MemberState) {
  return member.status === "ready" || member.status === "failed";
}

/**
 * Returns what a page should render for this member, given the member read and the region
 * resolution. One of four answers:
 *
 * - `loading` while either input is still pending.
 * - `failed` when the member read failed, carrying the message to show. The page still renders.
 * - `withheld` when both regions are known and differ, carrying the three facts the notice
 *   shows and the two regions it names.
 * - `ready` otherwise, carrying the member.
 *
 * The two inputs arrive independently and this waits for both. Answering on the member alone
 * would render the record for as long as the region request takes, which is the exposure the
 * guardrail exists to close.
 */
export function resolveMemberState(
  load: MemberLoad,
  region: RegionResolution,
  routeIdentifier: string
): MemberState {
  if (load.status === "loading") {
    return { status: "loading" };
  }

  if (load.status === "failed") {
    // The identifier in place of the name: it came from the QR code the clinician scanned,
    // so it still confirms who they are working on.
    return { status: "failed", name: routeIdentifier, message: load.message };
  }

  if (region.status === "resolving") {
    return { status: "loading" };
  }

  const servingRegion =
    region.status === "resolved" ? region.servingRegion : undefined;
  const memberRegion = memberDataRegion(load.details);
  const name = memberDisplayName(load.details, routeIdentifier);

  if (!shouldWithhold(servingRegion, memberRegion)) {
    return { status: "ready", name, details: load.details };
  }

  return {
    status: "withheld",
    name,
    externalId: load.details.externalId,
    // shouldWithhold returned true, so both regions are known
    memberRegion: memberRegion!,
    servingRegion: servingRegion!,
  };
}
